import { useMemo } from "react";

import { TiVoTargetsTable, ColoredText } from "components/elements";
import {
  Column,
  Row,
  Section,
  Subsection,
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
  SubTabs,
  SubTab,
} from "components/layout";
import { APP_TYPE } from "hooks/tivoApps";
import { NO_VALUE_PLACEHOLDER } from "src/constants";
import { trans } from "src/translations";

import {
  Countries,
  LaunchAttrsSection,
  PublicationsWarning,
  SubsectionWithFallback,
  SubsectionWithLink,
  TechnicalText,
  VariantButtons,
} from "../components";
import { ZoomConfigsSection } from "../components/ZoomConfigsSection";
import { TEXTS } from "../constants";
import styles from "../TiVoAppDetailsTabs.scss";
import { DetailsTabProps } from "../types";
import { getReleaseTabId, getReleaseTabLabel } from "../utils";

export function TiVoAppDetailsVariantTab({
  variantVersions,
  provider,
  selectedVariantVersionIndex = 0,
  onVariantVersionChange,
}: DetailsTabProps) {
  const version = variantVersions?.[selectedVariantVersionIndex];

  const nameValueMap = useMemo(
    () =>
      Object.entries(
        version?.uriTransform?.providerSpecificNameValueMap || {}
      ).map(([name, value]) => ({
        name,
        value,
      })),
    [version?.uriTransform?.providerSpecificNameValueMap]
  );

  if (!version) {
    return null;
  }

  const useProviderCountries = (version.countries?.length || 0) === 0;

  return (
    <>
      <SubTabs className={styles.tabs}>
        {variantVersions.map(({ label, releaseStages }, index) => (
          <SubTab
            // eslint-disable-next-line react/no-array-index-key
            key={`${getReleaseTabId(releaseStages)}-${index}`}
            active={selectedVariantVersionIndex === index}
            text={getReleaseTabLabel(
              releaseStages,
              label,
              variantVersions[0].label
            )}
            dataTestId={`${getReleaseTabId(releaseStages)}-tab`}
            onClick={() => onVariantVersionChange?.(index)}
          />
        ))}
      </SubTabs>
      <VariantButtons variantVersion={version} provider={provider} />
      <Row>
        <Column>
          <Section header={TEXTS.VARIANT.BASIC_SECTION_HEADER}>
            <Subsection header={TEXTS.VARIANT.VARIANT_LABEL_SUBS_HEADER}>
              {version.label || NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.VARIANT.APP_TYPE_SUBS_HEADER}>
              {APP_TYPE[version.launchAttributes?.appType] ||
                NO_VALUE_PLACEHOLDER}
            </Subsection>
            <Subsection header={TEXTS.VARIANT.ADS_SECTION_HEADER}>
              {version.adsEnabled ? trans.YES() : trans.NO()}
            </Subsection>
          </Section>
        </Column>
        <Column>
          <Section
            className={styles.targetSection}
            header={TEXTS.VARIANT.TARGET_SUBS_HEADER}
          >
            <TiVoTargetsTable targets={version.targets} />
            <PublicationsWarning
              releaseStages={version.releaseStages}
              targets={version.targets}
            />
          </Section>
        </Column>
      </Row>
      <LaunchAttrsSection attributes={version.launchAttributes} />
      <ZoomConfigsSection zoomConfigurations={version.zoomConfigurations} />
      <Section header={TEXTS.VARIANT.URI_TRANSFORM_SECTION_HEADER}>
        <Row>
          <Column>
            <Subsection header={TEXTS.VARIANT.STATUS_SUBS_HEADER}>
              <ColoredText
                color={version.uriTransform?.enabled ? "green" : "red"}
              >
                {version.uriTransform?.enabled
                  ? TEXTS.SHARED.ENABLED_LABEL
                  : TEXTS.SHARED.DISABLED_LABEL}
              </ColoredText>
            </Subsection>
          </Column>
        </Row>
        <Row>
          <Column>
            <SubsectionWithLink
              header={TEXTS.VARIANT.ALT_PLAYBACK_URI_SUBS_HEADER}
              href={version.uriTransform?.alternateBasePlaybackUrl}
            />
          </Column>
          <Column>
            <Subsection header={TEXTS.VARIANT.CLASS_NAME_SUBS_HEADER}>
              <TechnicalText>{version.uriTransform?.className}</TechnicalText>
            </Subsection>
          </Column>
        </Row>
        <Row>
          <Column>
            <SubsectionWithFallback
              header={TEXTS.VARIANT.NAME_VALUE_MAP_SUSB_HEADER}
              value={nameValueMap}
            >
              {(rows) => (
                <Table>
                  <TableHeader>
                    <TableHeaderColumn className={styles.nameCol}>
                      {TEXTS.VARIANT.NAME_COL_HEADER}
                    </TableHeaderColumn>
                    <TableHeaderColumn>
                      {TEXTS.VARIANT.VALUE_COL_HEADER}
                    </TableHeaderColumn>
                  </TableHeader>
                  <TableBody>
                    {rows.map(({ name, value }) => (
                      <TableRow key={name}>
                        <TableRowColumn>
                          <TechnicalText>{name}</TechnicalText>
                        </TableRowColumn>
                        <TableRowColumn>
                          <TechnicalText>{value}</TechnicalText>
                        </TableRowColumn>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SubsectionWithFallback>
          </Column>
        </Row>
      </Section>
      <Section header={TEXTS.PROVIDER.COUNTRIES_SECTION_HEADER}>
        {useProviderCountries ? (
          <Subsection>
            <div className={styles.inheritedCountries}>
              <span className={styles.inheritedIcon} />
              {TEXTS.VARIANT.INHERITED_COUNTRIES}
            </div>
          </Subsection>
        ) : null}
        <Countries
          countries={
            useProviderCountries ? provider.countries : version.countries
          }
        />
      </Section>
    </>
  );
}
